import { Link, NavLink, Outlet } from 'react-router-dom';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../../auth/AuthContext';
import {
  FiActivity,
  FiBarChart2,
  FiBell,
  FiCheckSquare,
  FiClipboard,
  FiGrid,
  FiHelpCircle,
  FiLayers,
  FiLogOut,
  FiPlusCircle,
  FiSearch,
  FiSettings,
  FiShield,
  FiSliders,
  FiUser,
  FiUsers,
  FiUserCheck,
} from 'react-icons/fi';

const primaryNavItems = [
  { to: '/dashboard', label: 'Dashboard', icon: FiGrid },
  { to: '/leads', label: 'Lead Pipeline', icon: FiLayers },
  { to: '/leads/new', label: 'Create Lead', icon: FiPlusCircle },
  { to: '/accounts', label: 'Accounts', icon: FiClipboard },
  { to: '/contacts', label: 'Contacts', icon: FiUsers },
  { to: '/activities', label: 'Activities', icon: FiActivity },
  { to: '/tasks', label: 'Tasks', icon: FiCheckSquare },
];

const operationsNavItems = [
  { to: '/agents', label: 'Agents', icon: FiUserCheck, allowedRoles: ['admin', 'manager'] },
  { to: '/assignment-rules', label: 'Assignment Rules', icon: FiSliders, allowedRoles: ['admin', 'manager'] },
  { to: '/queues', label: 'Lead Queues', icon: FiUsers, allowedRoles: ['admin', 'manager'] },
  { to: '/queue-worklist', label: 'Queue Worklist', icon: FiLayers, allowedRoles: ['admin', 'manager', 'agent'] },
  { to: '/reports', label: 'Reports', icon: FiBarChart2, allowedRoles: ['admin', 'manager'] },
  { to: '/audit-log', label: 'Audit Log', icon: FiShield, allowedRoles: ['admin', 'manager'] },
  { to: '/settings', label: 'Settings', icon: FiSettings, allowedRoles: ['admin', 'manager'] },
];

const SIDEBAR_AUTO_COLLAPSE_MS = 15000;

function AppLayout() {
  const { logout, role } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const collapseTimerRef = useRef(null);
  const [navTooltip, setNavTooltip] = useState({ text: '', x: 0, y: 0, visible: false });

  const clearCollapseTimer = useCallback(() => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
  }, []);

  const startCollapseTimer = useCallback(() => {
    clearCollapseTimer();
    collapseTimerRef.current = setTimeout(() => {
      setIsSidebarCollapsed(true);
    }, SIDEBAR_AUTO_COLLAPSE_MS);
  }, [clearCollapseTimer]);

  useEffect(() => {
    if (!isSidebarCollapsed) {
      startCollapseTimer();
    } else {
      clearCollapseTimer();
    }
    return clearCollapseTimer;
  }, [isSidebarCollapsed, startCollapseTimer, clearCollapseTimer]);

  const showNavTooltip = useCallback((e, label) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setNavTooltip({ text: label, x: rect.right + 12, y: rect.top + rect.height / 2, visible: true });
  }, []);

  const hideNavTooltip = useCallback(() => {
    setNavTooltip((t) => ({ ...t, visible: false }));
  }, []);

  const visibleOperationsNavItems = operationsNavItems.filter(
    (item) => !item.allowedRoles || item.allowedRoles.includes(role)
  );

  return (
    <div className={`app-shell ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {navTooltip.visible && (
        <div
          className="sidebar-nav-tooltip"
          style={{ left: navTooltip.x, top: navTooltip.y }}
        >
          {navTooltip.text}
        </div>
      )}
      <aside
        className="sidebar"
        onMouseEnter={clearCollapseTimer}
        onMouseLeave={() => { if (!isSidebarCollapsed) startCollapseTimer(); }}
      >
        <div className="sidebar-top-row">
          <Link className="brand" to="/dashboard">
            {isSidebarCollapsed ? (
              'LMS'
            ) : (
              
                 <span className="brand-subtext">Lead Management System</span>
              
            )}
          </Link>
          <button
            className="collapse-toggle-btn"
            type="button"
            onClick={() => setIsSidebarCollapsed((prev) => !prev)}
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isSidebarCollapsed ? '>' : '<'}
          </button>
        </div>

        <nav className="nav-menu">
          <p className="nav-group-title">Sales</p>
          {primaryNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/dashboard'}
                className="nav-item-link"
                onMouseEnter={isSidebarCollapsed ? (e) => showNavTooltip(e, item.label) : undefined}
                onMouseLeave={isSidebarCollapsed ? hideNavTooltip : undefined}
              >
                <span className="nav-item-icon" aria-hidden="true">
                  <Icon />
                </span>
                <span className="nav-item-label">{item.label}</span>
              </NavLink>
            );
          })}

          {visibleOperationsNavItems.length > 0 ? <p className="nav-group-title">Operations</p> : null}
          {visibleOperationsNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className="nav-item-link"
                onMouseEnter={isSidebarCollapsed ? (e) => showNavTooltip(e, item.label) : undefined}
                onMouseLeave={isSidebarCollapsed ? hideNavTooltip : undefined}
              >
                <span className="nav-item-icon" aria-hidden="true">
                  <Icon />
                </span>
                <span className="nav-item-label">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </aside>
      <main className="content">
        <header className="topbar">
          <div className="topbar-left">
            <img className="topbar-logo" src="/PRNB_logo.png" alt="PRNB" />
          </div>

          <div className="topbar-actions">
            <button className="icon-btn" type="button" title="Search">
              <FiSearch />
            </button>
            <button className="icon-btn" type="button" title="Notifications">
              <FiBell />
            </button>
            <button className="icon-btn" type="button" title="Help">
              <FiHelpCircle />
            </button>
            <button className="ghost-btn topbar-profile-btn" type="button" title="Profile">
              <FiUser /> Profile
            </button>
            <button className="ghost-btn topbar-logout-btn" onClick={logout} type="button" title="Logout">
              <FiLogOut /> Logout
            </button>
          </div>
        </header>
        <section className="page-body">
          <Outlet />
        </section>
      </main>
    </div>
  );
}

export default AppLayout;
